// Detalle de una reserva en PÁGINA (sin ventanas modales; sus acciones se abren
// en la misma página). Contrato: <ReservaDetalle reservaId={id} onClose={fn} />
// — lo usan Reservas, el Rack y Grupos reemplazando su contenido.
// Cabecera + noches + acciones según estado (tentativa/confirmada: depósito,
// asignar, modificar, check-in, cancelar · en_casa: folio + check-out ·
// cancelada/no_show: liquidar · salida: folio en solo lectura).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  Campo, Panel, fieldCls, money, hotelRpc, QK, fmtFecha, fmtCorta,
  ESTADO_RESERVA, type ReservaDetalleData, type Cotizacion, type Rack, type CheckoutResp, type Folio as FolioResp,useDialogoEnLinea } from './shared';
import { Folio } from './Folio';
import { BuscadorCliente, TIPOS_IDENT, useClientes, type ClienteOpt } from './ClienteHuesped';
import { AvisosPerfil, ModalFichaHuesped, TrazasReserva } from './Huesped360';
import { CobroHotel, nuevaLinea, pagosParaRpc, useCobroFormas, useValidarPagos, type LineaPago } from './CobroHotel';

export type Accion = null | 'identificar' | 'mover' | 'early' | 'late' | 'deposito' | 'asignar' | 'modificar' | 'modificarSalida' | 'checkin' | 'cancelar' | 'liquidar' | 'checkout';

// `accionInicial` abre la reserva directamente en una acción (atajos del rack).
export function ReservaDetalle({ reservaId, onClose, accionInicial = null }: { reservaId: string; onClose: () => void; accionInicial?: Accion }) {
  const qc = useQueryClient();
  const { data: r, isLoading } = useQuery<ReservaDetalleData>({
    queryKey: [QK, 'reserva', reservaId],
    queryFn: () => hotelRpc<ReservaDetalleData>('reserva_obtener', { _id: reservaId }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const [accion, setAccion] = useState<Accion>(null);
  const [ficha, setFicha] = useState(false);
  const inicialAplicada = useRef(false);
  useEffect(() => {
    if (!r || inicialAplicada.current || !accionInicial) return;
    inicialAplicada.current = true;
    // El depósito exige al huésped identificado, igual que el botón de la ficha.
    setAccion(accionInicial === 'deposito' && !r.huesped ? 'identificar' : accionInicial);
  }, [r, accionInicial]);

  const invalidarTodo = () => void qc.invalidateQueries({ queryKey: [QK] });
  const cerrarAccion = () => { setAccion(null); invalidarTodo(); };

  if (isLoading || !r) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  }

  const est = ESTADO_RESERVA[r.estado];
  const pendienteLiquidar = (r.estado === 'cancelada' || r.estado === 'no_show') && !r.penalidad_liquidada && (Number(r.penalidad) > 0 || Number(r.deposito_pagado) > 0);

  const enAccion = !!accion || ficha;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={enAccion ? () => { setAccion(null); setFicha(false); } : onClose}>
          <ArrowLeft className="mr-1 h-4 w-4" /> {enAccion ? `Volver a la reserva ${r.codigo}` : 'Volver'}
        </Button>
        <h1 className="text-xl font-semibold">Reserva {r.codigo}</h1>
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${est?.cls ?? ''}`}>{est?.label ?? r.estado}</span>
      </div>
      {!enAccion && (
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{r.codigo}</span>
              <span className={`rounded px-2 py-0.5 text-xs font-medium ${est?.cls ?? ''}`}>{est?.label ?? r.estado}</span>
            </div>
            <div className="text-muted-foreground">
              {r.huesped?.nombre ?? r.contacto_nombre ?? '—'}{r.huesped?.identificacion ? ` · ${r.huesped.identificacion}` : ''}
              {!r.huesped && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">sin identificar</span>}
              {r.huesped && <button type="button" className="ml-2 text-xs text-primary underline" onClick={() => setFicha(true)}>ficha del huésped</button>}
            </div>
            <AvisosPerfil perfil={r.perfil} />
            {(r.estado === 'tentativa' || r.estado === 'confirmada') && <LlegadaTardia r={r} reservaId={reservaId} />}
            <div className="text-xs text-muted-foreground">
              {fmtFecha(r.llegada)} → {fmtFecha(r.salida)} · {r.tipo.nombre}{r.habitacion ? ` · Hab. ${r.habitacion}` : ' · sin asignar'}
              {r.plan ? ` · ${r.plan}` : ''}
            </div>
          </div>
          <div className="text-right">
            <div className="text-base font-semibold">{money(r.total)}</div>
            <div className="text-xs text-muted-foreground">
              Depósito {money(r.deposito_pagado)} / {money(r.deposito_requerido)}
              {r.deposito_vence ? ` · vence ${fmtFecha(r.deposito_vence)}` : ''}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1.5 px-2">Noche</th><th className="py-1.5 px-2 text-right">Precio</th><th className="py-1.5 px-2 text-right">IVA</th></tr></thead>
            <tbody>
              {r.noches.map((n) => (
                <tr key={n.fecha} className={`border-b last:border-0 ${n.iva_pct === 8 ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                  <td className="py-1 px-2">{fmtCorta(n.fecha)}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{money(n.precio)}</td>
                  <td className="py-1 px-2 text-right tabular-nums">{n.iva_pct}%{n.iva_pct === 8 && <span className="ml-1 text-amber-600">feriado</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(r.estado === 'tentativa' || r.estado === 'confirmada') && !r.huesped && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <span>El huésped aún no está identificado. Se necesita su cédula, RUC o pasaporte para registrar el depósito (en el check-in se toma del registro).</span>
            <Button size="sm" onClick={() => setAccion('identificar')}>Identificar huésped</Button>
          </div>
        )}

        {(r.estado === 'tentativa' || r.estado === 'confirmada') && (
          <div className="flex flex-wrap gap-2 border-b pb-3">
            <Button size="sm" variant="outline" onClick={() => setAccion(r.huesped ? 'deposito' : 'identificar')}>Registrar depósito</Button>
            <Button size="sm" variant="outline" onClick={() => setAccion('asignar')}>{r.habitacion_id ? 'Cambiar' : 'Asignar'} habitación</Button>
            <Button size="sm" variant="outline" onClick={() => setAccion('modificar')}>Modificar</Button>
            <Button size="sm" onClick={() => setAccion('checkin')}>Check-in</Button>
            <Button size="sm" variant="destructive" onClick={() => setAccion('cancelar')}>Cancelar</Button>
          </div>
        )}

        {r.estado === 'en_casa' && (
          <>
            <div className="flex flex-wrap gap-2 border-b pb-3">
              <Button size="sm" variant="outline" onClick={() => setAccion('modificarSalida')}>Modificar salida</Button>
              <Button size="sm" variant="outline" onClick={() => setAccion('mover')}>Cambiar habitación</Button>
              <Button size="sm" variant="outline" onClick={() => setAccion('early')}>Early check-in</Button>
              <Button size="sm" variant="outline" onClick={() => setAccion('late')}>Late check-out</Button>
              <Button size="sm" onClick={() => setAccion('checkout')}>Check-out</Button>
            </div>
            <Folio reservaId={reservaId} />
          </>
        )}

        {pendienteLiquidar && (
          <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <span>Penalidad {money(r.penalidad)} · depósito pagado {money(r.deposito_pagado)} — pendiente de liquidar.</span>
            <Button size="sm" onClick={() => setAccion('liquidar')}>Liquidar</Button>
          </div>
        )}

        {r.estado === 'salida' && <Folio reservaId={reservaId} soloLectura />}

        {(r.movimientos ?? []).length > 0 && (
          <div className="rounded-md border p-2 text-xs">
            <div className="mb-1 font-medium">Cambios de habitación</div>
            {(r.movimientos ?? []).map((m, i) => (
              <div key={i} className="text-muted-foreground">
                {fmtFecha(m.fecha)} · {m.de ?? '—'} → <strong>{m.a}</strong> · {MODO_MOV[m.modo] ?? m.modo}
                {Number(m.diferencia) !== 0 && ` (${Number(m.diferencia) > 0 ? '+' : ''}${money(Number(m.diferencia))})`} · {m.motivo}
              </div>
            ))}
          </div>
        )}

        {r.notas && <p className="text-xs text-muted-foreground">Notas: {r.notas}</p>}

        {r.estado !== 'cancelada' && r.estado !== 'no_show' && <TrazasReserva reservaId={reservaId} trazas={r.trazas ?? []} />}
      </div>
      )}

      {ficha && r.huesped && <ModalFichaHuesped partnerId={r.huesped.id} onClose={() => setFicha(false)} />}
      {accion === 'mover' && <ModalMover r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {(accion === 'early' || accion === 'late') && <ModalHorario tipo={accion} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'identificar' && <ModalIdentificar r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'deposito' && <ModalDeposito r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'asignar' && <ModalAsignar r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {(accion === 'modificar' || accion === 'modificarSalida') && (
        <ModalModificar r={r} reservaId={reservaId} soloSalida={accion === 'modificarSalida'} onClose={() => setAccion(null)} onDone={cerrarAccion} />
      )}
      {accion === 'checkin' && <ModalCheckin r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'cancelar' && <ModalCancelar reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'liquidar' && <ModalLiquidar r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
      {accion === 'checkout' && <ModalCheckout r={r} reservaId={reservaId} onClose={() => setAccion(null)} onDone={cerrarAccion} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
const MODO_MOV: Record<string, string> = {
  mismo_tipo: 'mismo tipo', upgrade_cobrado: 'upgrade cobrado', upgrade_cortesia: 'upgrade de cortesía', cambio_tipo: 'cambio de tipo',
};
interface Libre { id: string; numero: string; piso: string | null; tipo: string; tipo_codigo: string; estado_limpieza: string; mismo_tipo: boolean; cabe: boolean; diferencia: number; noches: number }

function ModalMover({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const { hasAccessLevel } = usePermissions();
  const { data: libres = [], isLoading } = useQuery<Libre[]>({ queryKey: [QK, 'libres', reservaId], queryFn: () => hotelRpc<Libre[]>('habitaciones_libres', { _reserva: reservaId }) });
  const [sel, setSel] = useState<Libre | null>(null);
  const [motivo, setMotivo] = useState('');
  const [cortesia, setCortesia] = useState(false);
  const mover = useMutation({
    mutationFn: () => hotelRpc<{ habitacion: string; modo: string; diferencia: number }>('habitacion_mover', {
      _reserva: reservaId, _habitacion: sel?.id, _d: { motivo: motivo.trim(), cobrar_diferencia: !cortesia } }),
    onSuccess: (x) => { toast.success(`Huésped movido a la ${x.habitacion} (${MODO_MOV[x.modo] ?? x.modo}${Number(x.diferencia) ? `, ${money(Number(x.diferencia))}` : ''})`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const grupos = useMemo(() => {
    const m = new Map<string, Libre[]>();
    libres.filter((l) => l.cabe).forEach((l) => m.set(l.tipo, [...(m.get(l.tipo) ?? []), l]));
    return [...m.entries()];
  }, [libres]);
  return (
    <Panel title={`Cambiar habitación (hoy en la ${r.habitacion ?? '—'})`} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        {isLoading && <p className="text-muted-foreground">Buscando habitaciones libres…</p>}
        {!isLoading && grupos.length === 0 && <p className="text-muted-foreground">No hay habitaciones libres con capacidad hasta la salida.</p>}
        {grupos.map(([tipo, hs]) => (
          <div key={tipo}>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {tipo}{hs[0].mismo_tipo ? ' (mismo tipo)' : Number(hs[0].diferencia) > 0 ? ` · upgrade +${money(Number(hs[0].diferencia))} por ${hs[0].noches} noche(s)` : ` · ${money(Number(hs[0].diferencia))}`}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {hs.map((h) => (
                <button key={h.id} type="button" onClick={() => setSel(h)} title={`${h.piso ?? ''} · ${h.estado_limpieza}`}
                  className={`rounded border px-2.5 py-1 font-mono text-xs ${sel?.id === h.id ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'} ${h.estado_limpieza === 'sucia' ? 'opacity-60' : ''}`}>
                  {h.numero}
                </button>
              ))}
            </div>
          </div>
        ))}
        {sel && !sel.mismo_tipo && (
          <div className="rounded-md border border-sky-300 bg-sky-50 p-2 text-xs dark:border-sky-800 dark:bg-sky-900/20">
            Cambio a <strong>{sel.tipo}</strong>: {cortesia ? 'sin costo para el huésped (cortesía)' : `las noches que faltan se cobran a la tarifa nueva (${Number(sel.diferencia) >= 0 ? '+' : ''}${money(Number(sel.diferencia))} + impuestos)`}.
            {hasAccessLevel(4) && (
              <label className="mt-1 flex items-center gap-2"><input type="checkbox" checked={cortesia} onChange={(e) => setCortesia(e.target.checked)} /> Upgrade de cortesía (supervisor)</label>
            )}
          </div>
        )}
        <Campo label="Motivo *"><input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: aire acondicionado dañado, pidió vista al mar…" /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={mover.isPending} disabled={!sel || motivo.trim().length < 5} onClick={() => mover.mutate()}>Mover</Button>
        </div>
      </div>
    </Panel>
  );
}

function ModalHorario({ tipo, reservaId, onClose, onDone }: { tipo: 'early' | 'late'; reservaId: string; onClose: () => void; onDone: () => void }) {
  const [monto, setMonto] = useState('');
  const cargar = useMutation({
    mutationFn: () => hotelRpc<{ monto_base: number; iva_pct: number }>('cargo_horario', { _reserva: reservaId, _tipo: tipo, _monto: monto ? Number(monto) : null }),
    onSuccess: (x) => { toast.success(`Cargado ${money(Number(x.monto_base))} + IVA ${x.iva_pct} %`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const t = tipo === 'early' ? 'Early check-in' : 'Late check-out';
  return (
    <Panel title={t} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Se carga a la cuenta del alojamiento un porcentaje de la {tipo === 'early' ? 'primera' : 'última'} noche (según la configuración del hotel).
          Puede indicar otro monto; uno menor al de la política lo autoriza un supervisor.
        </p>
        <Campo label="Monto sin impuestos (vacío = según política)"><input type="number" min={0} step="0.01" className={fieldCls} value={monto} onChange={(e) => setMonto(e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={cargar.isPending} onClick={() => cargar.mutate()}>Cargar {t.toLowerCase()}</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Hora estimada de llegada y garantía (el cierre nocturno espera a la garantizada)
function LlegadaTardia({ r, reservaId }: { r: ReservaDetalleData; reservaId: string }) {
  const qc = useQueryClient();
  const [hora, setHora] = useState((r.hora_llegada ?? '').slice(0, 5));
  const guardar = useMutation({
    mutationFn: (garantizada: boolean) => hotelRpc('reserva_llegada', { _id: reservaId, _hora: hora || null, _garantizada: garantizada }),
    onSuccess: () => { toast.success('Llegada actualizada'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <span className="text-muted-foreground">Llega a las</span>
      <input type="time" className={`${fieldCls} h-7 w-28 text-xs`} value={hora} onChange={(e) => setHora(e.target.value)} onBlur={() => { if (hora !== (r.hora_llegada ?? '').slice(0, 5)) guardar.mutate(r.llegada_garantizada); }} />
      <label className="flex items-center gap-1" title="El cierre nocturno no la marca como no-show ese día; si llega después del cierre, en el check-in se carga la noche guardada">
        <input type="checkbox" checked={r.llegada_garantizada} disabled={guardar.isPending} onChange={(e) => guardar.mutate(e.target.checked)} /> Llegada tardía garantizada
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ModalIdentificar({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const clientes = useClientes();
  const [sel, setSel] = useState<ClienteOpt | null>(null);
  const [f, setF] = useState({ nombre: r.contacto_nombre ?? '', tipo_identificacion: 'CEDULA', identificacion: '', email: r.contacto_email ?? '', telefono: r.contacto_telefono ?? '' });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('reserva_identificar', { _id: reservaId, _huesped: sel
      ? { partner_id: sel.id, email: f.email.trim() || null, telefono: f.telefono.trim() || null }
      : { nombre: f.nombre.trim(), tipo_identificacion: f.tipo_identificacion, identificacion: f.identificacion.trim(), email: f.email.trim() || null, telefono: f.telefono.trim() || null } }),
    onSuccess: () => { toast.success('Huésped identificado'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const ok = !!sel || (!!f.nombre.trim() && !!f.identificacion.trim());
  return (
    <Panel title="Identificar huésped titular" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <BuscadorCliente clientes={clientes} seleccionado={sel} onSelect={setSel} />
        {!sel && (
          <div className="grid grid-cols-2 gap-2">
            <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} /></Campo>
            <Campo label="Tipo">
              <select className={fieldCls} value={f.tipo_identificacion} onChange={(e) => setF({ ...f, tipo_identificacion: e.target.value })}>
                {TIPOS_IDENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Campo>
            <Campo label="Identificación *"><input className={fieldCls} value={f.identificacion} onChange={(e) => setF({ ...f, identificacion: e.target.value })} /></Campo>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Email"><input type="email" className={fieldCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Campo>
          <Campo label="Teléfono"><input className={fieldCls} value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></Campo>
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!ok} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
function ModalDeposito({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const dlg = useDialogoEnLinea();
  const pendiente = Math.max(0, Math.round((Number(r.deposito_requerido) - Number(r.deposito_pagado)) * 100) / 100);
  const [objetivo, setObjetivo] = useState(pendiente);
  const [lineas, setLineas] = useState<LineaPago[]>(() => [nuevaLinea(pendiente)]);
  const v = useValidarPagos(lineas, objetivo);
  const { data: cf } = useCobroFormas();
  const esEfectivo = (id: string | null) => cf?.formas.find((f) => f.id === id)?.tipo === 'efectivo';
  const hayCobrado = lineas.some((l) => l.cobro && (l.cobro.estado === 'autorizado' || l.cobro.estado === 'pagado'));
  const registrar = useMutation({
    mutationFn: () => hotelRpc<{ total: number; estado: string }>('deposito_cobrar', { _id: reservaId, _pagos: (() => {
      // El efectivo de más es vuelto: no entra como depósito
      let vuelto = v.vuelto;
      return pagosParaRpc(lineas).map((p) => {
        if (vuelto > 0 && esEfectivo(p.forma_pago_id)) { const q = Math.min(vuelto, p.monto); vuelto -= q; return { ...p, monto: Math.round((p.monto - q) * 100) / 100 }; }
        return p;
      }).filter((p) => p.monto > 0);
    })() }),
    onSuccess: (x) => { toast.success(`Depósito registrado: ${money(Number(x.total))}${v.vuelto > 0 ? ` · vuelto ${money(v.vuelto)}` : ''}`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Panel title={`Registrar depósito · reserva ${r.codigo}`} onClose={async () => { if (hayCobrado && !(await dlg.confirmar('¿Salir sin registrar el depósito?', { detalle: 'Ya hay un cobro de la pasarela pagado. Si sale sin registrar, el huésped pagó y no quedará el depósito.', aceptar: 'Salir igual', peligro: true }))) return; onClose(); }}>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-end gap-3">
          <Campo label="Depósito a cobrar"><input type="number" min={0} step="0.01" className={`${fieldCls} w-40`} value={objetivo} onChange={(e) => setObjetivo(Number(e.target.value) || 0)} /></Campo>
          <span className="pb-2 text-xs text-muted-foreground">Requerido {money(r.deposito_requerido)} · pagado {money(r.deposito_pagado)} · total de la reserva {money(r.total)}</span>
        </div>
        <CobroHotel total={objetivo} lineas={lineas} onChange={setLineas} partnerId={r.titular_partner_id} exigirTotal={false} />
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={hayCobrado}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!v.ok || v.recibido <= 0} onClick={() => registrar.mutate()}>Registrar depósito</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
function ModalAsignar({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const { data: rack } = useQuery<Rack>({ queryKey: [QK, 'rack-asignar', reservaId, r.llegada, r.salida], queryFn: () => hotelRpc<Rack>('rack', { _desde: r.llegada, _hasta: r.salida }) });
  const [habId, setHabId] = useState(r.habitacion_id ?? '');

  const libres = useMemo(() => {
    if (!rack) return [];
    const ocupadas = new Set<string>();
    rack.reservas.forEach((x) => {
      if (x.id === reservaId) return;
      if (x.estado === 'cancelada' || x.estado === 'no_show') return;
      if (!x.habitacion_id) return;
      if (x.llegada < r.salida && r.llegada < x.salida) ocupadas.add(x.habitacion_id);
    });
    rack.bloqueos.forEach((b) => { if (b.habitacion_id && b.desde < r.salida && r.llegada < b.hasta) ocupadas.add(b.habitacion_id); });
    return rack.habitaciones.filter((h) => h.tipo_id === r.tipo_id && !ocupadas.has(h.id));
  }, [rack, r, reservaId]);

  const asignar = useMutation({
    mutationFn: () => hotelRpc('reserva_asignar', { _id: reservaId, _habitacion: habId || null }),
    onSuccess: () => { toast.success(habId ? 'Habitación asignada' : 'Habitación liberada'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Panel title="Asignar / cambiar habitación" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Habitación">
          <select className={fieldCls} value={habId} onChange={(e) => setHabId(e.target.value)}>
            <option value="">— Sin asignar —</option>
            {r.habitacion_id && !libres.some((h) => h.id === r.habitacion_id) && <option value={r.habitacion_id}>{r.habitacion} (actual)</option>}
            {libres.map((h) => <option key={h.id} value={h.id}>{h.numero}{h.piso ? ` · piso ${h.piso}` : ''}</option>)}
          </select>
        </Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={asignar.isPending} onClick={() => asignar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
function ModalModificar({ r, reservaId, soloSalida, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; soloSalida: boolean; onClose: () => void; onDone: () => void }) {
  const { isAdmin } = usePermissions();
  const [llegada, setLlegada] = useState(r.llegada);
  const [salida, setSalida] = useState(r.salida);
  const [tipoId] = useState(r.tipo_id);
  const [planId] = useState(r.plan_id ?? '');
  const [adultos, setAdultos] = useState(r.adultos);
  const [ninos, setNinos] = useState(r.ninos);
  const [cot, setCot] = useState<Cotizacion | null>(null);
  const [forzar, setForzar] = useState(false);

  const fechasValidas = salida > llegada;

  const preview = useMutation({
    mutationFn: () => hotelRpc<Cotizacion>('cotizar', { _tipo: tipoId, _plan: planId || null, _llegada: llegada, _salida: salida, _adultos: adultos, _ninos: ninos }),
    onSuccess: (data) => { setCot(data); setForzar(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: () => hotelRpc('reserva_modificar', {
      _id: reservaId,
      _d: { llegada, salida, tipo_id: tipoId, plan_id: planId || null, adultos, ninos, forzar: cot && cot.errores.length > 0 ? forzar : undefined },
    }),
    onSuccess: () => { toast.success('Reserva modificada'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = fechasValidas && (!cot || cot.errores.length === 0 || (forzar && isAdmin()));

  return (
    <Panel title={soloSalida ? 'Modificar salida' : 'Modificar reserva'} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <Campo label="Llegada"><input type="date" className={fieldCls} value={llegada} disabled={soloSalida} onChange={(e) => { setLlegada(e.target.value); setCot(null); }} /></Campo>
          <Campo label="Salida"><input type="date" className={fieldCls} min={llegada} value={salida} onChange={(e) => { setSalida(e.target.value); setCot(null); }} /></Campo>
          <Campo label="Adultos"><input type="number" min={1} className={fieldCls} value={adultos} disabled={soloSalida} onChange={(e) => { setAdultos(Math.max(1, Number(e.target.value) || 1)); setCot(null); }} /></Campo>
          <Campo label="Niños"><input type="number" min={0} className={fieldCls} value={ninos} disabled={soloSalida} onChange={(e) => { setNinos(Math.max(0, Number(e.target.value) || 0)); setCot(null); }} /></Campo>
        </div>
        <Button type="button" variant="outline" isLoading={preview.isPending} disabled={!fechasValidas} onClick={() => preview.mutate()}>Re-cotizar</Button>

        {cot && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span>Noches: <strong>{cot.noches_n}</strong></span>
              <span>Total: <strong>{money(cot.total)}</strong></span>
              <span>Depósito: <strong>{money(cot.deposito)}</strong></span>
            </div>
            {cot.errores.length > 0 && (
              <div className="space-y-1 rounded-md border border-red-400 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
                <ul className="list-disc pl-5">{cot.errores.map((e, i) => <li key={i}>{e}</li>)}</ul>
                {isAdmin() && (
                  <label className="flex items-center gap-2 pt-1">
                    <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} /> Forzar (administrador)
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!puedeGuardar} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
interface HuespedForm { nombres: string; tipo_documento: string; documento: string; nacionalidad: string; procedencia: string; fecha_nacimiento: string; es_titular: boolean;
  residencia: string; tct: string; motivo_viaje: string; responsable: string }
const esMenor = (nac: string) => { if (!nac) return false; const d = new Date(`${nac}T12:00:00`); const h = new Date(); h.setFullYear(h.getFullYear() - 18); return d > h; };

function ModalCheckin({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const { isAdmin } = usePermissions();
  const { data: rack } = useQuery<Rack>({ queryKey: [QK, 'rack-checkin', reservaId, r.llegada, r.salida], queryFn: () => hotelRpc<Rack>('rack', { _desde: r.llegada, _hasta: r.salida }), enabled: !r.habitacion_id });
  const [habId, setHabId] = useState(r.habitacion_id ?? '');
  const [forzar, setForzar] = useState(false);
  const [huespedes, setHuespedes] = useState<HuespedForm[]>([{
    nombres: r.huesped?.nombre ?? r.contacto_nombre ?? '', tipo_documento: 'cedula', documento: r.huesped?.identificacion ?? '',
    nacionalidad: '', procedencia: '', fecha_nacimiento: '', es_titular: true, residencia: 'turista', tct: '', motivo_viaje: 'turismo', responsable: '',
  }]);

  const libres = useMemo(() => {
    if (!rack) return [];
    const ocupadas = new Set<string>();
    rack.reservas.forEach((x) => {
      if (x.id === reservaId) return;
      if (x.estado === 'cancelada' || x.estado === 'no_show') return;
      if (!x.habitacion_id) return;
      if (x.llegada < r.salida && r.llegada < x.salida) ocupadas.add(x.habitacion_id);
    });
    return rack.habitaciones.filter((h) => h.tipo_id === r.tipo_id && !ocupadas.has(h.id));
  }, [rack, r, reservaId]);

  const setH = (i: number, patch: Partial<HuespedForm>) => setHuespedes((hs) => hs.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const agregar = () => setHuespedes((hs) => [...hs, { nombres: '', tipo_documento: 'cedula', documento: '', nacionalidad: '', procedencia: hs[0]?.procedencia ?? '', fecha_nacimiento: '', es_titular: false,
    residencia: hs[0]?.residencia ?? 'turista', tct: '', motivo_viaje: hs[0]?.motivo_viaje ?? 'turismo', responsable: '' }]);
  const quitar = (i: number) => setHuespedes((hs) => hs.filter((_, idx) => idx !== i));

  const checkin = useMutation({
    mutationFn: () => hotelRpc('checkin', {
      _id: reservaId,
      _d: {
        habitacion_id: habId || undefined,
        forzar: forzar || undefined,
        huespedes: huespedes.map((h) => ({
          nombres: h.nombres.trim(), tipo_documento: h.tipo_documento, documento: h.documento.trim(),
          nacionalidad: h.nacionalidad.trim() || null, procedencia: h.procedencia.trim() || null,
          fecha_nacimiento: h.fecha_nacimiento || null, es_titular: h.es_titular,
          residencia: h.residencia || null, tct: h.tct.trim() || null, motivo_viaje: h.motivo_viaje || null,
          responsable: esMenor(h.fecha_nacimiento) ? h.responsable.trim() || null : null,
        })),
      },
    }),
    onSuccess: (x: unknown) => {
      const n = Number((x as { noches_cargadas?: number })?.noches_cargadas ?? 0);
      toast.success(n > 0 ? `Check-in realizado · llegada tardía: se cargó ${n} noche(s) guardada(s)` : 'Check-in realizado');
      onDone();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = huespedes.every((h) => h.nombres.trim() && h.documento.trim() && (!esMenor(h.fecha_nacimiento) || h.responsable.trim()))
    && (!!habId || !!r.habitacion_id);

  return (
    <Panel title="Check-in" onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        {!r.habitacion_id && (
          <Campo label="Habitación">
            <select className={fieldCls} value={habId} onChange={(e) => setHabId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {libres.map((h) => <option key={h.id} value={h.id}>{h.numero}{h.piso ? ` · piso ${h.piso}` : ''}</option>)}
            </select>
          </Campo>
        )}
        <div className="space-y-2">
          {huespedes.map((h, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-md border p-2 md:grid-cols-6">
              <Campo label="Nombres *" className="md:col-span-2"><input className={fieldCls} value={h.nombres} onChange={(e) => setH(i, { nombres: e.target.value })} /></Campo>
              <Campo label="Tipo doc.">
                <select className={fieldCls} value={h.tipo_documento} onChange={(e) => setH(i, { tipo_documento: e.target.value })}>
                  <option value="cedula">Cédula</option><option value="pasaporte">Pasaporte</option><option value="ruc">RUC</option><option value="otro">Otro</option>
                </select>
              </Campo>
              <Campo label="Documento *"><input className={fieldCls} value={h.documento} onChange={(e) => setH(i, { documento: e.target.value })} /></Campo>
              <Campo label="Nacionalidad"><input className={fieldCls} value={h.nacionalidad} onChange={(e) => setH(i, { nacionalidad: e.target.value })} /></Campo>
              <div className="flex items-end justify-between gap-1">
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={h.es_titular} onChange={(e) => setH(i, { es_titular: e.target.checked })} /> Titular</label>
                {huespedes.length > 1 && (
                  <button type="button" className="rounded p-1 text-red-500 hover:bg-muted" onClick={() => quitar(i)}><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <Campo label="Procedencia"><input className={fieldCls} value={h.procedencia} placeholder="Ciudad / país" onChange={(e) => setH(i, { procedencia: e.target.value })} /></Campo>
              <Campo label="Fecha de nacimiento"><input type="date" className={fieldCls} value={h.fecha_nacimiento} onChange={(e) => setH(i, { fecha_nacimiento: e.target.value })} /></Campo>
              <Campo label="Residencia">
                <select className={fieldCls} value={h.residencia} onChange={(e) => setH(i, { residencia: e.target.value })}>
                  <option value="turista">Turista</option><option value="residente">Residente</option><option value="temporal">Residente temporal</option><option value="transeunte">Transeúnte</option>
                </select>
              </Campo>
              <Campo label="TCT (Galápagos)"><input className={fieldCls} value={h.tct} placeholder="N.º de tarjeta" onChange={(e) => setH(i, { tct: e.target.value })} /></Campo>
              <Campo label="Motivo de viaje">
                <select className={fieldCls} value={h.motivo_viaje} onChange={(e) => setH(i, { motivo_viaje: e.target.value })}>
                  <option value="turismo">Turismo</option><option value="negocios">Negocios</option><option value="familia">Visita familiar</option>
                  <option value="estudios">Estudios / investigación</option><option value="salud">Salud</option><option value="otro">Otro</option>
                </select>
              </Campo>
              {esMenor(h.fecha_nacimiento) ? (
                <Campo label="Adulto responsable *"><input className={fieldCls} value={h.responsable} onChange={(e) => setH(i, { responsable: e.target.value })} /></Campo>
              ) : <div />}
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={agregar}><Plus className="mr-1 h-3.5 w-3.5" /> Acompañante</Button>
        </div>
        {isAdmin() && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={forzar} onChange={(e) => setForzar(e.target.checked)} /> Forzar (habitación sucia / fuera de servicio, overbooking, etc.)
          </label>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={checkin.isPending} disabled={!puedeGuardar} onClick={() => checkin.mutate()}>Check-in</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
function ModalCancelar({ reservaId, onClose, onDone }: { reservaId: string; onClose: () => void; onDone: () => void }) {
  const { isAdmin } = usePermissions();
  const [motivo, setMotivo] = useState('');
  const [sinPenalidad, setSinPenalidad] = useState(false);
  const [resultado, setResultado] = useState<{ penalidad: number; deposito_pagado: number; a_devolver: number; pendiente_liquidar: boolean } | null>(null);

  const cancelar = useMutation({
    mutationFn: () => hotelRpc<{ penalidad: number; deposito_pagado: number; a_devolver: number; pendiente_liquidar: boolean }>('reserva_cancelar', { _id: reservaId, _motivo: motivo.trim(), _sin_penalidad: sinPenalidad }),
    onSuccess: (r) => { toast.success('Reserva cancelada'); setResultado(r); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (resultado) {
    return (
      <Panel title="Reserva cancelada" onClose={onDone}>
        <div className="space-y-2 text-sm">
          <div>Penalidad: <strong>{money(resultado.penalidad)}</strong></div>
          <div>Depósito pagado: <strong>{money(resultado.deposito_pagado)}</strong></div>
          <div>A devolver: <strong>{money(resultado.a_devolver)}</strong></div>
          {resultado.pendiente_liquidar && <p className="text-xs text-amber-700 dark:text-amber-300">Queda pendiente de liquidar — usa el botón «Liquidar» en el detalle.</p>}
          <div className="flex justify-end border-t pt-3"><Button onClick={onDone}>Cerrar</Button></div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Cancelar reserva" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Motivo * (mín. 5 caracteres)"><textarea className={`${fieldCls} h-auto`} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        {isAdmin() && (
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={sinPenalidad} onChange={(e) => setSinPenalidad(e.target.checked)} /> Cancelar sin penalidad (administrador)
          </label>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Volver</Button>
          <Button variant="destructive" isLoading={cancelar.isPending} disabled={motivo.trim().length < 5} onClick={() => cancelar.mutate()}>Confirmar cancelación</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
function ModalLiquidar({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const aDevolver = Math.max(0, Number(r.deposito_pagado) - Number(r.penalidad));
  const [forma, setForma] = useState<'efectivo' | 'tarjeta' | 'transferencia'>('efectivo');
  const [referencia, setReferencia] = useState('');
  const [emitir, setEmitir] = useState(true);

  const liquidar = useMutation({
    mutationFn: () => hotelRpc('cancelacion_liquidar', { _id: reservaId, _d: { devolucion_forma: aDevolver > 0 ? forma : null, referencia: referencia.trim() || null, emitir } }),
    onSuccess: (r: unknown) => {
      const x = r as { emision_error?: string | null; devuelto?: number };
      toast.success(`Cancelación liquidada${Number(x?.devuelto ?? 0) > 0 ? ` · devuelto ${money(Number(x.devuelto))}` : ''}`);
      if (x?.emision_error) toast.warning(`La factura de la penalidad no se envió al SRI: ${x.emision_error}`);
      onDone();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Panel title="Liquidar cancelación" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div>Penalidad: <strong>{money(r.penalidad)}</strong></div>
        <div>Depósito pagado: <strong>{money(r.deposito_pagado)}</strong></div>
        <div>A devolver: <strong>{money(aDevolver)}</strong></div>
        {aDevolver > 0 && (
          <>
            <Campo label="Forma de devolución">
              <select className={fieldCls} value={forma} onChange={(e) => setForma(e.target.value as typeof forma)}>
                <option value="efectivo">Efectivo</option><option value="tarjeta">Tarjeta</option><option value="transferencia">Transferencia</option>
              </select>
            </Campo>
            <Campo label="Referencia"><input className={fieldCls} value={referencia} onChange={(e) => setReferencia(e.target.value)} /></Campo>
          </>
        )}
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={emitir} onChange={(e) => setEmitir(e.target.checked)} /> Emitir documento de la penalidad</label>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={liquidar.isPending} onClick={() => liquidar.mutate()}>Liquidar</Button>
        </div>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function ModalCheckout({ r, reservaId, onClose, onDone }: { r: ReservaDetalleData; reservaId: string; onClose: () => void; onDone: () => void }) {
  const dlg = useDialogoEnLinea();
  const { data: folio } = useQuery<FolioResp>({ queryKey: [QK, 'folio', reservaId], queryFn: () => hotelRpc<FolioResp>('folio_obtener', { _reserva: reservaId }), staleTime: 0, refetchOnMount: 'always' });
  const { data: pre } = useQuery<{ saldo: number; noche_pendiente: number }>({ queryKey: [QK, 'checkout-pre', reservaId], queryFn: () => hotelRpc('checkout_preliminar', { _id: reservaId }), staleTime: 0 });
  const [lineas, setLineas] = useState<LineaPago[] | null>(null);
  const [emitir, setEmitir] = useState(true);
  const [devForma, setDevForma] = useState<'efectivo' | 'transferencia' | 'tarjeta'>('efectivo');
  const [devRef, setDevRef] = useState('');
  const [resultado, setResultado] = useState<CheckoutResp | null>(null);
  const hayDeposito = Number(folio?.deposito_disponible ?? 0) > 0.009;

  // Saldo por cuenta, sumando la noche de hoy si aún no se cargó (salida el mismo día)
  const ventanas = useMemo(() => {
    const vs = (folio?.ventanas ?? []).filter((v) => !v.ticket_id).map((v) => ({ numero: v.numero, nombre: v.nombre, saldo: Number(v.saldo) }));
    const np = Number(pre?.noche_pendiente ?? 0);
    if (np > 0 && vs.length) {
      const dest = (folio?.ventanas ?? []).find((v) => !v.ticket_id && (v.rutas ?? []).includes('hospedaje')) ?? folio?.ventanas.find((v) => v.numero === 1);
      const x = vs.find((v) => v.numero === dest?.numero) ?? vs[0];
      x.saldo = Math.round((x.saldo + np) * 100) / 100;
    }
    return vs;
  }, [folio, pre]);
  const huesped = ventanas.find((v) => v.numero === 1);
  const aCobrar = Math.max(0, huesped?.saldo ?? 0);
  const [iniciales] = useState(() => nuevaLinea(0, 1));
  const filas = lineas ?? (aCobrar > 0 ? [{ ...iniciales, monto: aCobrar }] : []);
  const v = useValidarPagos(filas, aCobrar);
  const hayCobrado = filas.some((l) => l.cobro && (l.cobro.estado === 'autorizado' || l.cobro.estado === 'pagado'));

  const checkout = useMutation({
    mutationFn: () => hotelRpc<CheckoutResp>('checkout', {
      _id: reservaId,
      _d: { pagos: pagosParaRpc(filas), emitir, devolucion_forma: hayDeposito ? devForma : null, devolucion_referencia: devRef.trim() || null },
    }),
    onSuccess: (x) => setResultado(x),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (resultado) {
    return (
      <Panel title={`Check-out realizado · ${r.codigo}`} onClose={onDone}>
        <div className="space-y-3 text-sm">
          {Number(resultado.vuelto ?? 0) > 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-lg font-semibold text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">Vuelto a entregar: {money(Number(resultado.vuelto))}</p>}
          {Number(resultado.deposito_devuelto ?? 0) > 0 && <p className="rounded-md bg-sky-50 px-3 py-2 text-sky-900 dark:bg-sky-900/20 dark:text-sky-200">Depósito devuelto al huésped: <strong>{money(Number(resultado.deposito_devuelto))}</strong> ({devForma})</p>}
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-muted-foreground">
              <th className="py-1.5 pr-2">Cuenta</th><th className="py-1.5 pr-2">Factura</th><th className="py-1.5 pr-2 text-right">Total</th>
              <th className="py-1.5 pr-2 text-right">Depósito aplicado</th><th className="py-1.5 pr-2 text-right">Pagado</th><th className="py-1.5 pr-2 text-right">Saldo a crédito</th>
            </tr></thead>
            <tbody>
              {resultado.facturas.map((f, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 pr-2">{f.ventana}</td>
                  <td className="py-1.5 pr-2">{f.serie ?? (f.ticket_id ? 'por emitir' : '—')}{Number(f.facturas ?? 1) > 1 && <span className="ml-1 text-muted-foreground">(+{Number(f.facturas) - 1} parcial)</span>}{f.emision_error && <div className="text-red-600">⚠ {f.emision_error}</div>}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(f.total)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(f.deposito_aplicado)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(f.pagado)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(f.saldo_credito)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end border-t pt-3"><Button onClick={onDone}>Listo</Button></div>
        </div>
      </Panel>
    );
  }

  if (!folio || !pre) return <Panel title="Check-out" onClose={onClose}><div className="py-6 text-center text-sm text-muted-foreground">Cargando folio…</div></Panel>;

  return (
    <Panel title={`Check-out · ${r.codigo} · ${r.huesped?.nombre ?? r.contacto_nombre ?? ''}`}
      onClose={async () => { if (hayCobrado && !(await dlg.confirmar('¿Salir sin confirmar el check-out?', { detalle: 'Ya hay un cobro de la pasarela pagado. Si sale sin confirmar el check-out, el huésped pagó y no quedará registrado.', aceptar: 'Salir igual', peligro: true }))) return; onClose(); }}>
      {dlg.ui}
      <div className="space-y-3 text-sm">
        <div className="grid gap-2 md:grid-cols-3">
          {ventanas.map((w) => (
            <div key={w.numero} className="rounded-md border p-2">
              <div className="text-xs text-muted-foreground">Cuenta {w.numero} · {w.nombre}</div>
              <div className="text-lg font-semibold tabular-nums">{money(w.saldo)}</div>
              {w.numero !== 1 && <div className="text-[11px] text-muted-foreground">Lo no cobrado aquí queda a crédito de la empresa</div>}
            </div>
          ))}
        </div>
        {Number(pre.noche_pendiente) > 0 && <p className="text-xs text-muted-foreground">Incluye la noche de hoy ({money(Number(pre.noche_pendiente))}), que se carga al confirmar.</p>}
        <CobroHotel total={aCobrar} lineas={filas} onChange={setLineas} partnerId={r.titular_partner_id} ventanas={ventanas} />
        {hayDeposito && (
          <div className="grid grid-cols-2 gap-2 rounded-md border border-sky-300 bg-sky-50 p-2 dark:border-sky-800 dark:bg-sky-900/20">
            <p className="col-span-2 text-xs text-sky-900 dark:text-sky-200">
              Hay {money(Number(folio.deposito_disponible))} de depósito sin aplicar: se aplica a las facturas y lo que sobre se devuelve al huésped.
            </p>
            <Campo label="Devolver por">
              <select className={fieldCls} value={devForma} onChange={(e) => setDevForma(e.target.value as typeof devForma)}>
                <option value="efectivo">Efectivo</option><option value="transferencia">Transferencia</option><option value="tarjeta">Reverso a la tarjeta</option>
              </select>
            </Campo>
            <Campo label="Referencia"><input className={fieldCls} value={devRef} onChange={(e) => setDevRef(e.target.value)} /></Campo>
          </div>
        )}
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={emitir} onChange={(e) => setEmitir(e.target.checked)} /> Emitir factura(s) al SRI</label>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={hayCobrado}>Cancelar</Button>
          <Button isLoading={checkout.isPending} disabled={!v.ok && filas.length > 0} onClick={() => checkout.mutate()}>Confirmar check-out</Button>
        </div>
      </div>
    </Panel>
  );
}
